'use client';

import type { CSSProperties, InputHTMLAttributes, LabelHTMLAttributes, ReactNode, Ref } from 'react';
import { forwardRef } from 'react';
import styled from 'styled-components';

import { Spacing } from '@zoomit/primitives';

import { theme } from '../../theme';
import { Typography } from '../typography';

type RadioButtonVariant = 'lg' | 'md';

const Styled = styled.div<{ variant: RadioButtonVariant; alignItems?: CSSProperties['alignItems'] }>`
  display: flex;
  align-items: ${({ alignItems }) => (alignItems ? alignItems : 'unset')};
  font-size: 1.2rem;
  color: var(${theme.grey5});

  input[type='radio'] {
    appearance: none;
    border-radius: var(${theme.radius.full});
    width: ${({ variant }) => (variant === 'md' ? '1.2rem' : '1.6rem')};
    height: ${({ variant }) => (variant === 'md' ? '1.2rem' : '1.6rem')};
    cursor: pointer;
    background-color: transparent;
    position: relative;
    top: ${({ variant }) => (variant === 'md' ? '1' : '0')}px;
    margin-left: ${Spacing.x2}rem;
  }

  input[type='radio']::after {
    content: '';
    box-sizing: border-box;
    display: block;
    width: 100%;
    height: 100%;
    border-radius: var(${theme.radius.full});
    border: solid 1px var(${theme.grey5});
  }

  input[type='radio']:disabled {
    cursor: not-allowed;
  }

  input[type='radio']:checked {
    background: radial-gradient(white 20%, var(${theme.primary2}) 10%);
  }

  input[type='radio']:checked::after {
    border-color: var(${theme.primary2});
  }

  input[type='radio']:hover:disabled {
    cursor: not-allowed;
  }
`;

type RadioBaseProps = {
  label?: string | ReactNode;
  value?: string;
  variant?: RadioButtonVariant;
  alignItems?: CSSProperties['alignItems'];
  id?: string;
  labelProps?: LabelHTMLAttributes<HTMLLabelElement>;
};

export type RadioProps = InputHTMLAttributes<HTMLInputElement> & RadioBaseProps;

export const RadioButton = forwardRef((props: RadioProps, ref?: Ref<HTMLInputElement>) => {
  const { label, value, variant = 'md', alignItems, id, labelProps, ...rest } = props;

  return (
    <Styled variant={variant} alignItems={alignItems}>
      <input type="radio" id={id || value} value={value} {...rest} ref={ref} />
      {label && (
        <label {...labelProps} htmlFor={id || value}>
          <Typography fontSize={1.3}>{label}</Typography>
        </label>
      )}
    </Styled>
  );
});
